import { Icon, Box } from 'vopay';
import { format, fromUnixTime, isSameDay, parseISO } from 'date-fns';

import { useHistory } from 'react-router-dom';
import ElinxForm from './ElinxForm';
import { ElinxInfo } from './types';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';

import { useDataFetchIndicator } from '@/hooks';
import { useConfirmDialog } from '@/providers/ConfirmDialog.provider';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import useContact from './useContact';

const ElinxCollectContainer: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [, , , sendElinx] = usePostSendPaylink({});
  const [, , , postEmbedUrl] = usePostEmbedUrl({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.EConnect' });
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'Elinx' });
  const { getContact, saveContactEmail } = useContact();

  const onSubmit = async (values: ElinxInfo) => {
    try {
      const processedScheduleStartDate = processDate(values.ScheduleStartDate);
      const processedScheduleEndDate = processDate(values.ScheduleEndDate);

      let frequency;
      let FormattedScheduleStartDate;
      let FormattedScheduleEndDate;
      let NameOfFrequency;

      if (isToday(processedScheduleStartDate) && values.Frequency === 'single') {
        FormattedScheduleStartDate = undefined;
        FormattedScheduleEndDate = undefined;
        frequency = undefined;
        NameOfFrequency = undefined;
      } else {
        FormattedScheduleStartDate = processedScheduleStartDate;
        FormattedScheduleEndDate = processedScheduleEndDate;
        frequency = values.Frequency;
        NameOfFrequency = values.NameOfFrequency;
      }
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const embedUrlResponse = await postEmbedUrl({
        ...values,
        RedirectURL: '',
        RedirectMethod: 'JavascriptMessage',
        ClientControlled: false,
        Language: 'en',
        Version: 'v2',
        ClientAccountID: values?.Contact,
      });

      if (!embedUrlResponse.Success) {
        throw new Error(embedUrlResponse.ErrorMessage);
      }
      const elinxResponse = await sendElinx({
        ...values,
        ScheduleStartDate: FormattedScheduleStartDate,
        ScheduleEndDate:
          FormattedScheduleEndDate && values.EndingAfterPayments === undefined ? FormattedScheduleEndDate : undefined,
        Frequency: frequency,
        NameOfFrequency: NameOfFrequency,
        IframeKey: embedUrlResponse.IframeKey,
        ReceiverEmailAddress: values.OverrideEmailAddress || values.NewEmailAddress || '',
        ReceiverName: values?.ExistingContactName || `${values.FirstName || ''} ${values.LastName || ''}`,
        ReceiverPhoneNumber: values?.ReceiverPhoneNumber?.replace('+', ''),
        Amount: undefined,
        PaymentType: undefined,
        ClientAccountID: values?.Contact,
        PaymentMethodDisplayOptions: 'New',
      });
      if (!elinxResponse.Success) {
        throw new Error(elinxResponse.ErrorMessage);
      }

      const contactResponse = await getContact({
        ContactID: values.Contact,
      });

      if (contactResponse.EmailAddress === '' && values.OverrideEmailAddress) {
        try {
          await saveContactEmail({
            ContactID: values.Contact,
            EmailAddress: values.OverrideEmailAddress,
          });
        } catch (err) {
          toast.neutral(tElinx('savingEmailError', { defaultValue: 'Failed to save the email address' }));
        }
      }

      const sendNotificationMethod = values?.SendNotification ? values?.NotificationMethod : 'none';
      history.push(
        `/elinx-history/connect/confirmation/${elinxResponse.ELinxRequestID}/${sendNotificationMethod}/none`,
      );
    } catch (err) {
      console.error(err);
      const subTitle = err instanceof Error ? err.message : undefined;
      confirmDialog.error(t('SendPaylinkError.title'), {
        subTitle,
        cancelLabel: t('SendPaylinkError.cancelLabel'),
      });
    } finally {
      setDataFetchingInfo(false);
    }
  };
  function processDate(timestamp: string | undefined) {
    if (timestamp === undefined) {
      return undefined;
    }
    const date = fromUnixTime(Number(timestamp) / 1000);
    if (isNaN(date.getTime())) {
      return undefined;
    }

    return format(date, 'yyyy-MM-dd');
  }
  function isToday(dateString: string | undefined) {
    if (!dateString || dateString === undefined) {
      return false;
    }

    const today = new Date();
    const date = parseISO(dateString);

    return isSameDay(date, today);
  }
  return (
    <Box flx="row" jc="center">
      <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
        <Box flx="col" gapSize="32px">
          <Title>
            <Box ai="baseline" gapSize="4px">
              <Icon icon="eLinxE" height="18px" width="18px" />
              {t('title')}
            </Box>
          </Title>
          <div>{tElinx('Cards.EConnect.description')}</div>
        </Box>

        <Box base="8" pt="xmd">
          <ElinxForm onSubmit={onSubmit} formType="Connect" />
        </Box>
      </Box>
    </Box>
  );
};

export default ElinxCollectContainer;
